import axiosInstance from '../utils/axios.ts';
import { getCentralOffsetStringForDate } from '../utils/timezone.ts';
// Every successful mutation below marks the cached data it changed as stale (see data/queryClient.ts).
// Keeping that HERE, at the one place every write passes through, means no screen can forget to.
import { invalidateEntities } from '../data/invalidation.ts';

// Test mode feature flag
export const isTestModeEnabled = () => {
  return process.env.REACT_APP_TEST_MODE_ENABLED === 'true';
};

const api = axiosInstance;

export interface Employee {
  employeeId: string;
  employeeType: 'nurse' | 'doctor' | 'nurse-student-junior' | 'nurse-student-senior';
  firstName: string;
  lastName: string;
  homeHospitalId?: string;
  credentials?: string[];
  shiftOrder?: number;
  isFloatable?: boolean; // Only for doctors - if false, doctor cannot float (must be allocated alone)
  maxConcurrentNurseCoverageRatio?: number; // Only for doctors - max concurrent ORs with nurse coverage
  maxConcurrentStudentCoverageRatio?: number; // Only for doctors - max concurrent ORs with senior student coverage
  isCardiacAnesthesiologist?: boolean; // Only for doctors - Symphony staffs cardiac rooms only with these
  studentCompetency?: 'new' | 'intermediate' | 'green'; // Only for students - new/intermediate/green competency
  // Nurses AND doctors (see backend types.ts). Special-category health data: the API returns it to
  // Admins only, so it is absent for read-only users — never read its absence as "not pregnant".
  isPregnant?: boolean;
}

export interface Shift {
  employeeId: string;
  startTimeUtc: string;
  endTimeUtc: string;
  employeeType: 'nurse' | 'doctor' | 'nurse-student-junior' | 'nurse-student-senior';
  order?: number; // Only for doctor shifts - represents pecking order (lower number = higher priority)
}

export interface Hospital {
  hospitalId: string;
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  latitude?: number;
  longitude?: number;
  color?: string; // Board display color (hex); editable on the Hospitals page
  floatCrnaSlots?: number; // Site-level FLOAT CRNA slot count — the Board draws this many empty "Float CRNA"
                           // boxes under the site's doctor coins. Set per-site by the Simulate tab.
  // SITE TYPE — drives Symphony's surgery-center and children's-hospital staffing rules server-side
  // (backend/src/utils/site-classification.ts). Toggled per site on the Hospitals page. Undefined means
  // "not yet marked", and the two flags then behave DIFFERENTLY: an unmarked surgery center is still
  // classified by a deprecated hospital-NAME heuristic, but an unmarked children's hospital is staffed as
  // an ordinary ADULT site — pediatric status is flag-only and the name confers nothing (answer #2).
  isSurgeryCenter?: boolean;
  isChildrensHospital?: boolean;
  // The site's IANA time zone name (e.g. "America/Chicago"), used server-side to place inbound feeds'
  // local wall-clock times in UTC. Undefined means "not recorded". Offsets and abbreviations are
  // rejected by the backend — see components/hospitalTimezones.ts for the zones offered.
  timezone?: string;
}

export interface OperatingRoom {
  operatingRoomId: string;
  roomNumber: string;
  name: string;
  hospitalId: string;
  credential?: string; // Required credential for staff to work in this OR
  // ROOM TYPE — drives Symphony's cardiac reservation and OB carve-out server-side
  // (backend/src/utils/room-classification.ts). Undefined means "not yet marked", and the backend
  // falls back to a deprecated regex over the case's surgeryType for that room.
  isCardiacRoom?: boolean;
  isCSectionRoom?: boolean;
  isHazard?: boolean; // Indicates if this operating room is hazardous
  isDoctorAssignableOnly?: boolean; // If true, only doctors can be assigned (no nurses/students)
  hasIonizingRadiation?: boolean; // Manual override: treat as ionizing-radiation (blocks pregnant CRNAs)
}

export interface OperatingRoomSchedule {
  scheduleId: string;
  operatingRoomId: string;
  startTimeUtc: string;
  endTimeUtc: string;
  surgeryType: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  allocatedNurseIds: string[];
  allocatedDoctorIds: string[];
}

export interface ManualAllocation {
  scheduleId: string;
  allocatedNurseIds: string[];
  allocatedDoctorIds: string[];
}

export interface AllocationRequest {
  startTimeUtc: string;
  endTimeUtc: string;
  manualAllocations?: ManualAllocation[];
  maxGapMinutes?: number;
  minBreakMinutes?: number;
  algorithm?: 'cpsat' | 'greedy' | 'greedy-morning' | 'symphony';
  timezone?: string;
  siteLocks?: Record<string, string>; // employeeId -> hospitalId hard lock (Symphony)
  floatPreassignments?: Record<string, string[]>; // hospitalId -> CRNA employeeIds pre-assigned to float slots (Symphony)
  floatSlotsByHospital?: Record<string, number>; // hospitalId -> per-day float SLOT count for this date (Symphony)
  offEmployeeIds?: string[]; // providers marked "Off" (sent home) — HARD-unavailable to the allocator (Symphony)
  pediatricScheduleIds?: string[]; // schedules flagged pediatric on the board (⚑) — Symphony prefers a pediatric doctor for it as a tiebreak; the 1:3 ratio applies only at pediatric sites (Symphony)
  peckingOrderOverride?: Record<string, number>; // employeeId -> pecking # as reordered on the Anesthesiologists tab, so Symphony's waterfall follows it (Symphony)
}

// Allocation types based on the new logic
export type AllocationType = 'crna-doctor' | 'doctor-only' | 'senior-student-doctor';

export interface AllocationDetails {
  scheduleId: string;
  allocationType: AllocationType;
  allocatedNurseIds: string[];
  allocatedDoctorIds: string[];
  doctorFloatingLimit?: number; // 4 for CRNA+Doctor, 2 for Senior Student+Doctor, 1 for Doctor Only
}

export interface AllocationResult {
  success: boolean;
  allocations: AllocationDetails[];
  errors?: string[];
  // Symphony-only: site-level FLOAT CRNA assignments (hospitalId -> CRNA employeeIds). The board writes these
  // into its per-day float overlay so the float boxes show who the algorithm assigned.
  floatCrnaAssignments?: { hospitalId: string; crnaIds: string[] }[];
  // Symphony-only: a handoff PARTWAY THROUGH a case — the case stays with whoever started it and employeeId takes over
  // the role at handoffUtc. The board writes these into its relief overlay (reliefOverlay.applyReliefHandoffs).
  reliefHandoffs?: { scheduleId: string; role: 'doctor' | 'crna'; employeeId: string; fromEmployeeId?: string; handoffUtc: string }[];
  // Symphony-only: a CRNA held for an add-on C-section at an OB hospital with nothing scheduled in its OB room. The
  // room has no cases, so there is no allocation — the board draws this CRNA on that room's plate.
  obHoldCrnas?: { hospitalId: string; operatingRoomId: string; crnaIds: string[] }[];
  // Rooms Symphony could not staff, with WHY (reason). `no credentialed ...` means nobody working today
  // holds the credentials for that room, which the board draws as NO CRED rather than NEED MD.
  unstaffedRooms?: { operatingRoomId: string; hospitalId: string; hospitalName: string; roomLabel: string; scheduleIds: string[]; missing: 'crna' | 'anesthesiologist' | 'both'; reason: string }[];
  summary: {
    totalSchedules: number;
    totalAllocated: number;
    totalHours: number;
    allocationBreakdown: {
      crnaDoctor: number;
      doctorOnly: number;
      seniorStudentDoctor: number;
    };
    employeesUsed: {
      nurses: string[];
      doctors: string[];
    };
  };
}

// Employee API
export const createEmployee = async (employee: Omit<Employee, 'employeeId'>) => {
  const employeeData = { ...employee };

  // If employee type is not 'doctor', remove doctor-specific fields entirely
  if (employeeData.employeeType !== 'doctor') {
    delete employeeData.isFloatable;
    delete employeeData.maxConcurrentNurseCoverageRatio;
    delete employeeData.maxConcurrentStudentCoverageRatio;
    delete employeeData.isCardiacAnesthesiologist;
  }
  const isStudentType = employeeData.employeeType === 'nurse-student-junior' || employeeData.employeeType === 'nurse-student-senior';
  if (!isStudentType) {
    delete employeeData.studentCompetency;
  }

  const response = await api.post('/employees', employeeData);
  invalidateEntities('employees');
  return response.data;
};

export const updateEmployee = async (employeeId: string, employee: Omit<Employee, 'employeeId'>) => {
  // Create a copy of the employee object to avoid mutating the original
  const employeeData = { ...employee };

  // If employee type is not 'doctor', remove doctor-specific fields entirely
  if (employeeData.employeeType !== 'doctor') {
    delete employeeData.isFloatable;
    delete employeeData.maxConcurrentNurseCoverageRatio;
    delete employeeData.maxConcurrentStudentCoverageRatio;
    delete employeeData.isCardiacAnesthesiologist;
  }
  const isStudentType = employeeData.employeeType === 'nurse-student-junior' || employeeData.employeeType === 'nurse-student-senior';
  if (!isStudentType) {
    delete employeeData.studentCompetency;
  }

  const response = await api.put(`/employees/${employeeId}`, employeeData);
  invalidateEntities('employees');
  return response.data;
};

// Shift API
export const createShift = async (shift: Shift) => {
  const response = await api.post('/shifts', shift);
  invalidateEntities('shifts');
  return response.data;
};

/**
 * Edit one shift. PARTIAL: omitted fields are left alone. Pass `order: null` to clear a pecking order
 * (omitting it means "leave as is"). employeeId/employeeType are rejected by the handler -- identity is
 * immutable, so re-assigning a shift means delete + create.
 */
export const updateShift = async (
  shiftId: string,
  patch: { startTimeUtc?: string; endTimeUtc?: string; order?: number | null }
) => {
  const response = await api.put(`/shifts/${encodeURIComponent(shiftId)}`, patch);
  invalidateEntities('shifts');
  return response.data;
};

/** Delete ONE shift. wipeShifts(day) is the whole-day reset; this is the single-cell clear. */
export const deleteShift = async (shiftId: string) => {
  const response = await api.delete(`/shifts/${encodeURIComponent(shiftId)}`);
  invalidateEntities('shifts');
  return response.data;
};

export const bulkCreateShifts = async (shifts: Array<{
  employeeId: string;
  startTimeUtc: string;
  endTimeUtc: string;
  employeeType: string;
  order?: number;
}>) => {
  const response = await api.post('/bulk-create-shifts', { shifts });
  invalidateEntities('shifts');
  return response.data;
};

export const searchShifts = async (startTimeUtc: string, endTimeUtc: string) => {
  const response = await api.get('/shifts', {
    params: { startTimeUtc, endTimeUtc }
  });
  return response.data;
};

// Hospital API
export const createHospital = async (hospital: Omit<Hospital, 'hospitalId'>) => {
  const response = await api.post('/hospitals', hospital);
  invalidateEntities('hospitals', 'operatingRooms');
  return response.data;
};

// The editable fields of a hospital. `name` is always sent (the backend requires it); every other field
// is optional, and OMITTING one leaves the stored value alone.
//   color: a hex sets it; '' resets it to the default (id-derived) colour.
//   address: must be complete (all five parts) when sent; it can't be cleared.
//   latitude / longitude: a number sets it; null clears it.
//   Site-type flags are tri-state, matching the backend: true/false pins the site, null CLEARS it.
//     Clearing isSurgeryCenter hands the site back to the deprecated name heuristic; clearing
//     isChildrensHospital makes it an ordinary ADULT site — it does not become pediatric-by-name again,
//     because it never was (answer #2). See backend/src/utils/site-classification.ts.
// A location change does not recompute hospital distances; call calculateHospitalDistances() after it.
export interface HospitalUpdate {
  name: string;
  color?: string;
  address?: Hospital['address'];
  latitude?: number | null;
  longitude?: number | null;
  isSurgeryCenter?: boolean | null;
  isChildrensHospital?: boolean | null;
  // Same tri-state: omit to leave it, an IANA zone name to set it, null to clear it.
  timezone?: string | null;
}

export const updateHospital = async (hospitalId: string, update: HospitalUpdate) => {
  // Drop undefined fields so they are omitted on the wire (left unchanged) rather than sent.
  const body = Object.fromEntries(Object.entries(update).filter(([, v]) => v !== undefined));
  const response = await api.put(`/hospitals/${encodeURIComponent(hospitalId)}`, body);
  invalidateEntities('hospitals');
  return response.data;
};

// What a hospital delete removes (returned by the dryRun preview and the real delete).
export interface HospitalDeleteSummary {
  hospitalId: string;
  hospitalName: string;
  operatingRooms: number;
  schedules: number;
  allocations: number;
  distanceRows: number;
  employeesHomeBaseCleared: number;
}

// Deletes a hospital and everything under it (rooms, schedules, allocations, distance
// links); staff who called it home are kept with their home base cleared. Pass
// { dryRun: true } to preview the counts without deleting anything.
export const deleteHospital = async (
  hospitalId: string,
  opts?: { dryRun?: boolean }
): Promise<{ dryRun?: boolean; wouldDelete?: HospitalDeleteSummary; message?: string; deleted?: HospitalDeleteSummary }> => {
  const response = await api.delete(`/hospitals/${encodeURIComponent(hospitalId)}`, {
    params: opts?.dryRun ? { dryRun: 'true' } : undefined,
  });
  // The server cascades a real delete to the site's rooms, cases and allocations, and clears the home
  // base of staff who called it home — so all of those are stale, not just the hospital list.
  if (!opts?.dryRun) invalidateEntities('hospitals', 'operatingRooms', 'schedules', 'allocations', 'employees');
  return response.data;
};

// Operating Room API
export const createOperatingRoom = async (operatingRoom: Omit<OperatingRoom, 'operatingRoomId'>) => {
  const response = await api.post('/operating-rooms', operatingRoom);
  invalidateEntities('operatingRooms');
  return response.data;
};

// Every field is optional (a partial update). credential '' clears it; the two room-type flags are
// tri-state like the site-type flags — null CLEARS one, handing that room back to the backend's
// surgery-type text fallback (backend/src/utils/room-classification.ts). A room can't change hospital.
export interface OperatingRoomUpdate {
  name?: string;
  roomNumber?: string;
  credential?: string;
  isHazard?: boolean;
  isDoctorAssignableOnly?: boolean;
  hasIonizingRadiation?: boolean;
  isCardiacRoom?: boolean | null;
  isCSectionRoom?: boolean | null;
}

export const updateOperatingRoom = async (operatingRoomId: string, updates: OperatingRoomUpdate) => {
  const response = await api.put(`/operating-rooms/${encodeURIComponent(operatingRoomId)}`, updates);
  invalidateEntities('operatingRooms');
  return response.data;
};

export const deleteOperatingRoom = async (operatingRoomId: string) => {
  const response = await api.delete(`/operating-rooms/${encodeURIComponent(operatingRoomId)}`);
  invalidateEntities('operatingRooms', 'schedules', 'allocations');
  return response.data;
};

// Operating Room Schedule API
export const createOperatingRoomSchedule = async (schedule: Omit<OperatingRoomSchedule, 'scheduleId' | 'status' | 'allocatedNurseIds' | 'allocatedDoctorIds'>) => {
  const response = await api.post('/operating-room-schedules', schedule);
  invalidateEntities('schedules');
  return response.data;
};

export const searchOperatingRoomSchedules = async (startTimeUtc: string, endTimeUtc: string) => {
  const response = await api.get('/operating-room-schedules', {
    params: { startTimeUtc, endTimeUtc }
  });
  return response.data;
};

export const updateOperatingRoomSchedule = async (scheduleId: string, updates: { surgeryType: string }) => {
  const response = await api.put(`/operating-room-schedules/${encodeURIComponent(scheduleId)}`, updates);
  invalidateEntities('schedules');
  return response.data;
};

export const deleteOperatingRoomSchedule = async (scheduleId: string) => {
  const response = await api.delete(`/operating-room-schedules/${encodeURIComponent(scheduleId)}`);
  invalidateEntities('schedules', 'allocations');
  return response.data;
};

// Allocation Job Status
export type AllocationJobStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface AllocationJobResponse {
  jobId: string;
  status: AllocationJobStatus;
  result?: AllocationResult;
  error?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Allocation API (unified async starter - routes to greedy or CP-SAT based on algorithm field)
export const startAllocation = async (request: AllocationRequest): Promise<{ jobId: string; status: AllocationJobStatus }> => {
  const response = await api.post('/start-allocation', request);
  return response.data;
};

export const getAllocationJobStatus = async (jobId: string): Promise<AllocationJobResponse> => {
  const response = await api.get('/allocation-job-status', {
    params: { jobId }
  });
  return response.data;
};

// Backward compat alias
export const allocateSchedules = startAllocation;

export const searchAllocationsByWindow = async (startTimeUtc: string, endTimeUtc: string) => {
  const response = await api.get('/allocations', {
    params: { startTimeUtc, endTimeUtc }
  });
  return response.data;
};

export const updateAllocations = async (allocations: Array<{scheduleId: string, allocatedNurseIds: string[], allocatedDoctorIds: string[], colorOverrides?: Record<string, string>}>, startTimeUtc: string, endTimeUtc: string) => {
  const response = await api.put('/allocations', {
    allocations,
    startTimeUtc,
    endTimeUtc
  });
  invalidateEntities('allocations');
  return response.data;
};

// Grading loop (LOCAL-ONLY): snapshot Symphony as the baseline, publish the runner's edited day for grading,
// and read a published day back. These persist server-side (DynamoDB) so a graded pair survives refresh/rebuild.
export const saveGradingBaseline = async (date: string, algorithm: string, allocations: any[]) => {
  const response = await api.post('/grading/baseline', { date, algorithm, allocations });
  return response.data;
};
export const publishGradingDay = async (date: string, final: any[], moveLog: any[]) => {
  const response = await api.post('/grading/publish', { date, final, moveLog });
  return response.data;
};
export const getGradingDay = async (date?: string) => {
  const response = await api.get('/grading', { params: date ? { date } : {} });
  return response.data;
};

// Credentials API
export const getCredentials = async () => {
  const response = await api.get('/credentials');
  return response.data;
};

// Pass the SAME local-day UTC window the board uses to fetch/display allocations, so the wipe deletes exactly
// what's on screen (a UTC-day-only wipe misses late local-evening cases and leaves the Allocate button stuck).
export const wipeAllocations = async (day: string, startTimeUtc?: string, endTimeUtc?: string) => {
  const response = await api.post('/wipe-allocations', { day, startTimeUtc, endTimeUtc });
  invalidateEntities('allocations');
  return response.data;
};

export const wipeShifts = async (day: string): Promise<{ message: string; deletedCount: number }> => {
  const response = await api.post('/wipe-shifts', { day });
  invalidateEntities('shifts');
  return response.data;
};

// Hospital Distances API
export interface HospitalDistance {
  fromHospitalId: string;
  toHospitalId: string;
  distanceKm: number;
  distanceMiles: number;
  fromHospitalName: string;
  toHospitalName: string;
  calculatedAt: string;
}

export const calculateHospitalDistances = async () => {
  const response = await api.post('/calculate-hospital-distances');
  return response.data;
};

export const getHospitalDistances = async (fromHospitalId?: string) => {
  const response = await api.get('/hospital-distances', {
    params: fromHospitalId ? { fromHospitalId } : undefined
  });
  return response.data;
};

// GET APIs
export const getEmployees = async () => {
  const response = await api.get('/employees');
  return response.data;
};

export const getHospitals = async () => {
  const response = await api.get('/hospitals');
  return response.data;
};

export const getOperatingRooms = async () => {
  const response = await api.get('/operating-rooms');
  return response.data;
};

// Deprecated: use searchShifts(start,end)
export const searchShiftsByDay = async (day: string) => {
  const response = await api.get('/shifts-by-day', {
    params: { day }
  });
  return response.data;
};

// Employee type display name mapping
export const getEmployeeTypeDisplayName = (employeeType: string): string => {
  const displayNames: Record<string, string> = {
    'nurse': 'CRNA',
    'doctor': 'Doctor',
    'nurse-student-junior': 'CRNA Student - Junior',
    'nurse-student-senior': 'CRNA Student - Senior'
  };
  return displayNames[employeeType] || employeeType;
};

// Allocation type display name mapping
export const getAllocationTypeDisplayName = (allocationType: AllocationType): string => {
  const displayNames: Record<AllocationType, string> = {
    'crna-doctor': 'CRNA + Doctor (Floating)',
    'doctor-only': 'Doctor Only',
    'senior-student-doctor': 'Senior Student + Doctor (Limited Floating)'
  };
  return displayNames[allocationType] || allocationType;
};

// Simulation API
export interface SimulateParams {
  date: string;
  numDoctorShifts?: number;      // pecking-order doctors (full day, ranked)
  numPeckingCrnas?: number;      // pecking-order CRNAs (full day, ranked)
  numShiftDoctors1500?: number;  // shift doctors, out at 3:00 PM (15:00)
  numShiftDoctors1700?: number;  // shift doctors, out at 5:00 PM (17:00)
  numShiftCrnas1500?: number;    // shift CRNAs, out at 3:00 PM (15:00)
  numShiftCrnas1700?: number;    // shift CRNAs, out at 5:00 PM (17:00)
  numShiftCrnas1900?: number;    // shift CRNAs, out at 7:00 PM (19:00)
  numShiftStudents1500?: number; // shift senior students, out at 3:00 PM (15:00)
  numShiftStudents1700?: number; // shift senior students, out at 5:00 PM (17:00)
  numShiftDoctorShifts?: number; // legacy random-time shift doctors (test pipelines only)
  // Per-site OR selection: { hospitalId: numberOfRoomsToOpen }. The tab sends this; the
  // test pipelines send numSchedules instead.
  siteOrCounts?: Record<string, number>;
  // Per-site FLOAT CRNA count: { hospitalId: numberOfFloatCrnaSlots }. Persisted onto each site's
  // hospital record (floatCrnaSlots) so the Board draws that many empty float boxes under its coins.
  floatCrnaCountBySite?: Record<string, number>;
  // Shift roster the tab sends (replaces the individual role counts):
  //  - open-in (doctors/students): available early, arrival derived, out at outLocalHour
  //  - window (CRNAs): fixed startLocalHour -> endLocalHour (may cross midnight)
  //  - pecking (doctors/CRNAs): full-day + rank
  shiftSpecs?: Array<{
    role: 'doctor' | 'crna' | 'student';
    count: number;
    mode: 'open-in' | 'window' | 'pecking';
    outLocalHour?: number;
    startLocalHour?: number;
    endLocalHour?: number;
  }>;
  // Dedicated specialty rooms (one room per requested specialist, 1:1):
  //  - numCardiacDocs: opens that many CARDIAC-only rooms, each staffed by a cardiac
  //    anesthesiologist (fresh doctors are flagged cardiac as needed).
  //  - numObDocs: opens that many OB (cesarean-only) rooms; any doctor covers them.
  // Set to 0 (default) to generate NO cardiac/cesarean cases at all.
  numCardiacDocs?: number;
  numObDocs?: number;
  // Pediatric anesthesiologists on the roster (required to supervise at children's hospitals).
  numPediatricDocs?: number;
  numNurseShifts?: number;
  numSeniorStudentShifts?: number;
  numSchedules?: number;
  concurrencyDegree?: number;
  randomSeed?: number;
  distributionStrategy?: 'pure-random' | 'clustered' | 'realistic' | 'morning-surge';
  minProcedureDuration?: number;
  maxProcedureDuration?: number;
  // When set (local hour, e.g. 9.5 = 9:30am), every generated OR schedule starts
  // before this local time. Omit for the all-day distribution.
  latestStartLocalHour?: number;
  // When true, the day's existing simulated shifts/cases/allocations are cleared before
  // generating, so a run REPLACES the day instead of stacking on top of earlier runs.
  replaceDay?: boolean;
}

export const simulateDay = async (params: SimulateParams) => {
  // Pin simulation to US Central time (America/Chicago) for the SIMULATED date, so
  // generated shift + case times are unambiguously Central regardless of what
  // timezone this computer is set to — avoiding UTC/Central miscommunication. The
  // date-specific lookup keeps daylight-saving correct (CDT = UTC-5, CST = UTC-6).
  const timezone = getCentralOffsetStringForDate(params.date);

  const response = await api.post('/simulate', { ...params, timezone });
  invalidateEntities('shifts', 'schedules', 'allocations', 'hospitals', 'employees');
  return response.data;
};

// Bulk Import Shifts API
export interface BulkImportShiftsResult {
  success: boolean;
  message: string;
  shiftsCreated: number;
  totalParsed: number;
  errors: Array<{
    location: string;
    name: string;
    window: string;
    error: string;
  }>;
  shifts: Array<{
    employeeName: string;
    firstName: string;
    lastName: string;
    timeWindow: string;
    startTimeUtc: string;
    endTimeUtc: string;
    cellLocation: string;
  }>;
}

export const bulkImportShifts = async (fileData: string): Promise<BulkImportShiftsResult> => {
  const response = await api.put('/bulk-import-shifts', { fileData });
  invalidateEntities('shifts');
  return response.data;
};

// External Mappings API
export interface ExternalMapping {
  mappingType: 'location' | 'room';
  externalKey: string;
  internalId: string;
  displayLabel?: string;
  updatedAt?: string;
}

export interface ScheduleImportResult {
  success: boolean;
  schedulesCreated: number;
  schedulesSkipped: number;
  totalRowsParsed: number;
  unmappedLocations: string[];
  unmappedRooms: string[];
  errors: string[];
}

export const getExternalMappings = async (mappingType?: string) => {
  const response = await api.get('/external-mappings', {
    params: mappingType ? { mappingType } : undefined
  });
  return response.data;
};

export const upsertExternalMapping = async (mapping: Omit<ExternalMapping, 'updatedAt'>) => {
  const response = await api.put('/external-mappings', mapping);
  invalidateEntities('externalMappings');
  return response.data;
};

export const bulkUpsertExternalMappings = async (mappings: Omit<ExternalMapping, 'updatedAt'>[]) => {
  const response = await api.put('/external-mappings', { bulk: true, mappings });
  invalidateEntities('externalMappings');
  return response.data;
};

export const deleteExternalMapping = async (mappingType: string, externalKey: string) => {
  const response = await api.delete('/external-mappings', {
    data: { mappingType, externalKey }
  });
  invalidateEntities('externalMappings');
  return response.data;
};

export const importOperatingRoomSchedules = async (params: { jsonpData: string; dryRun?: boolean }): Promise<ScheduleImportResult> => {
  const response = await api.post('/import-operating-room-schedules', params);
  if (!params.dryRun) invalidateEntities('schedules');
  return response.data;
};

// ---- Tenant-scoped user management (gate G3) ----
// The customer (tenant) is NEVER sent from the client — the backend derives it from the
// caller's verified token claim and force-stamps it. These calls only carry email/role.
export type TenantRole = 'Admin' | 'User';

export interface TenantUser {
  username: string;
  email: string | null;
  enabled?: boolean;
  status?: string;
  role: TenantRole;
}

export const listUsers = async (): Promise<{ users: TenantUser[]; count: number }> => {
  const response = await api.get('/users');
  return response.data;
};

export const createUser = async (email: string, role: TenantRole = 'User') => {
  const response = await api.post('/users', { email, role });
  invalidateEntities('users');
  return response.data;
};

export const setUserRole = async (username: string, role: TenantRole) => {
  const response = await api.put('/users', { username, role });
  invalidateEntities('users');
  return response.data;
};

export const setUserEnabled = async (username: string, enabled: boolean) => {
  const response = await api.put('/users', { username, enabled });
  invalidateEntities('users');
  return response.data;
};

// ---- Stripe billing (per-tenant subscriptions) ----
// As with user management, the tenant is NEVER sent from the client — the backend derives it
// from the verified token claim. Checkout/Portal return a URL the browser redirects to; no
// card data or Stripe key ever touches the frontend.
export interface BillingSubscription {
  hasSubscription: boolean;
  active: boolean;
  status: string; // 'active' | 'trialing' | 'past_due' | 'canceled' | 'none' | ...
  priceId: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /**
   * True when this tenant is currently write-blocked by the subscription paywall.
   *
   * Computed server-side because it folds together three things the SPA cannot see: the
   * BILLING_ENFORCEMENT_ENABLED flag, the exemption allowlist, and the status. Do NOT derive
   * it from `status` — with enforcement off, or for a grandfathered tenant, an inactive
   * subscription still writes normally. Older backends omit it, hence optional.
   */
  readOnly?: boolean;
  /** True when the paywall is switched on for this environment at all. */
  enforcementEnabled?: boolean;
}

export const getBillingSubscription = async (): Promise<BillingSubscription> => {
  const response = await api.get('/billing/subscription');
  return response.data;
};

export const createBillingCheckoutSession = async (): Promise<{ url: string }> => {
  const response = await api.post('/billing/checkout', {});
  return response.data;
};

export const createBillingPortalSession = async (): Promise<{ url: string }> => {
  const response = await api.post('/billing/portal', {});
  return response.data;
};
